import { ColumnDef } from '@tanstack/react-table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge.tsx';
import {
  DeviceTypeCodes,
  DeviceTypeNames,
  DEVICE_TYPE_NAME_BY_CODE,
  TTreeDevice,
} from '@/features/devices/schemas/devices.schemas.ts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TTranslationFunction = (...args: any[]) => any;

const getLabelDevice = (deviceType: DeviceTypeNames | undefined) => {
  if (deviceType === 'Inverter') return 'blue';
  if (deviceType === 'BMS') return 'purple';
  if (deviceType === 'SmartMeter') return 'green';
  if (deviceType === 'SmartLogger') return 'indigo';
  if (deviceType === 'Dongle') return 'pink';
  if (deviceType === 'Logger') return 'yellow';
  if (deviceType === 'PQConnect') return 'red';
  if (deviceType === 'Battery') return 'blue';
  if (deviceType === 'Unknown') return 'gray';
  return 'gray';
};

export const getColumns = ({
  isEditable,
  t,
}: {
  isEditable: boolean;
  t: TTranslationFunction;
}): ColumnDef<TTreeDevice>[] => {
  return [
    {
      accessorKey: 'name',
      header: ({ table }) => (
        <button
          onClick={table.getToggleAllRowsExpandedHandler()}
          className="flex items-center gap-2"
          type="button"
        >
          {table.getIsAllRowsExpanded() ? (
            <ChevronDown className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          )}
          <span>{t('devices.deviceName')}</span>
        </button>
      ),
      cell: ({ row, getValue }) => (
        <div style={{ paddingLeft: `${row.depth * 1.4}rem` }}>
          <div className="flex items-center gap-2 text-nowrap">
            {row.getCanExpand() ? (
              <button
                onClick={row.getToggleExpandedHandler()}
                className="text-muted-foreground flex items-center gap-2"
                style={{ cursor: 'pointer' }}
                type="button"
              >
                {row.getIsExpanded() ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
              </button>
            ) : (
              <span className="size-4" />
            )}
            {isEditable ? (
              <span>{getValue<string>()}</span>
            ) : !row.original.showInDashboard ? (
              <span>{getValue<string>()}</span>
            ) : (
              <Link
                to={`/plants/${row.original.plantId}/devices/${row.original.deviceId}`}
                className="link-underline text-blue-600"
              >
                {getValue<string>()}
              </Link>
            )}
          </div>
        </div>
      ),
      footer: (props) => props.column.id,
    },
    {
      size: 100,
      accessorKey: 'sn',
      header: () => <div className="text-nowrap">{t('devices.details.serialNumber')}</div>,
      cell: ({ row }) => <div className="text-nowrap">{row.getValue('sn')}</div>,
    },
    {
      size: 100,
      accessorKey: 'typeId',
      header: t('devices.details.deviceType.title'),
      cell: ({ row }) => {
        const typeName = DEVICE_TYPE_NAME_BY_CODE[row.getValue<DeviceTypeCodes>('typeId')];
        return (
          <Badge className="text-nowrap" variant={getLabelDevice(typeName)}>
            {typeName}
          </Badge>
        );
      },
    },
    {
      size: 100,
      accessorKey: 'supplier',
      header: t('devices.details.supplier'),
      cell: ({ row }) => <div className="text-nowrap">{row.getValue('supplier')}</div>,
    },
    {
      size: 100,
      accessorKey: 'model',
      header: t('devices.details.model'),
      cell: ({ row }) => <div className="text-nowrap">{row.getValue('model')}</div>,
    },
  ];
};
